/**
 * Project schemas.
 *
 * A project is the central entity. It carries DFA dates, the parts-workflow state,
 * locations, financial metadata (earnest, warranty period), and closure flags.
 *
 * Bank Guarantee instruments live in their own table (many per project) — see
 * bank-guarantee.ts.
 *
 * Contacts live in project_departments + project_department_contacts — see
 * project-department.ts.
 */

import { Type, type Static } from '@sinclair/typebox';
import {
  InspectionLocations,
  PartsWorkflowStates,
  ProjectCurrencies,
  ProjectLifecycleStates,
} from '../enums.js';
import { FIELD_LIMITS, NUMERIC_LIMITS } from '../constants.js';
import {
  IsoDateSchema,
  IsoDateTimeSchema,
  PaisaSchema,
  RowVersionField,
  SoftDeleteFields,
  TimestampFields,
  UuidSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Read shape — stored fields
// ---------------------------------------------------------------------------

export const ProjectSchema = Type.Object({
  id: UuidSchema,

  // Identity
  projectTitle: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.PROJECT_TITLE_MAX }),
  projectNumber: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.PROJECT_NUMBER_MAX }),
  clientId: UuidSchema,

  // Currency / foreign principal details
  projectCurrency: Type.Union(ProjectCurrencies.map((c) => Type.Literal(c))),
  otherCurrencyCode: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.CURRENCY_CODE_MAX }),
    Type.Null(),
  ]),
  principalName: Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  principalAddress: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX }),
    Type.Null(),
  ]),
  principalContactPerson: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }),
    Type.Null(),
  ]),
  principalEmail: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX }),
    Type.Null(),
  ]),
  principalPhone: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }),
    Type.Null(),
  ]),
  principalFax: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }),
    Type.Null(),
  ]),
  portOfShipment: Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),

  // Foreign principal bank details
  principalBankName: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }),
    Type.Null(),
  ]),
  principalBankAccountNumber: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.BANK_ACCOUNT_MAX }),
    Type.Null(),
  ]),
  principalBankIban: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.BANK_ACCOUNT_MAX }),
    Type.Null(),
  ]),
  principalBankAccountTitle: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }),
    Type.Null(),
  ]),
  principalBankSwiftCode: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.SWIFT_CODE_MAX }),
    Type.Null(),
  ]),
  principalBankAddress: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX }),
    Type.Null(),
  ]),
  principalBankEmail: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX }),
    Type.Null(),
  ]),
  principalBankPhone: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }),
    Type.Null(),
  ]),
  principalBankFax: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }),
    Type.Null(),
  ]),

  // DFA dates
  dfaReceivedDate: IsoDateSchema, // required at creation
  dfaSubmittedDate: Type.Union([IsoDateSchema, Type.Null()]),
  contractSignDate: Type.Union([IsoDateSchema, Type.Null()]),

  // Historical bid context
  dateOfIssuance: Type.Union([IsoDateSchema, Type.Null()]),
  technicalBidOpeningDate: Type.Union([IsoDateSchema, Type.Null()]),
  financialBidOpeningDate: Type.Union([IsoDateSchema, Type.Null()]),

  // Delivery commitments
  deliveryDurationMonths: Type.Union([
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.DELIVERY_DURATION_MONTHS_MAX }),
    Type.Null(),
  ]),
  /** Derived from contractSignDate + deliveryDurationMonths; read-only. */
  effectiveDeliveryTargetDate: Type.Union([IsoDateSchema, Type.Null()]),
  gracePeriodDays: Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.GRACE_DAYS_MAX }),

  // Contract / indent metadata
  origin: Type.Union([Type.String({ maxLength: FIELD_LIMITS.ORIGIN_MAX }), Type.Null()]),
  indentNumber: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.INDENT_NUMBER_MAX }),
    Type.Null(),
  ]),
  indentDate: Type.Union([IsoDateSchema, Type.Null()]),
  makerNameBrand: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.MAKER_NAME_BRAND_MAX }),
    Type.Null(),
  ]),

  // Project-level commercial terms. The derived GST amount / total are exposed
  // on ProjectWithStatusSchema and are not accepted as write inputs.
  totalStoreValuePaisa: Type.Union([PaisaSchema, Type.Null()]),
  gstRateBps: Type.Union([
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
    Type.Null(),
  ]),
  requiredBgAmountPaisa: Type.Union([PaisaSchema, Type.Null()]),
  bgSubmissionDurationDays: Type.Union([
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.BG_SUBMISSION_DAYS_MAX }),
    Type.Null(),
  ]),
  bgValidityDurationMonths: Type.Union([
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.BG_VALIDITY_MONTHS_MAX }),
    Type.Null(),
  ]),
  termsOfDelivery: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.INCOTERM_CODE_MAX }),
    Type.Null(),
  ]),
  paymentOnINoteBps: Type.Union([
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
    Type.Null(),
  ]),
  paymentOnCrvBps: Type.Union([
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
    Type.Null(),
  ]),
  partialDeliveryAllowed: Type.Boolean(),
  partialPaymentAllowed: Type.Boolean(),
  partialDeliveryPaymentRemarks: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }),
    Type.Null(),
  ]),

  // Financial (BG instruments live on bank_guarantees)
  earnestMoneyAmountPaisa: Type.Union([PaisaSchema, Type.Null()]),
  earnestSubmittedDate: Type.Union([IsoDateSchema, Type.Null()]),
  earnestReleasedDate: Type.Union([IsoDateSchema, Type.Null()]),

  warrantyPeriodMonths: Type.Union([
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.WARRANTY_MONTHS_MAX }),
    Type.Null(),
  ]),

  // Locations
  consigneeLocation: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX }),
    Type.Null(),
  ]),
  defaultInspectionLocation: Type.Union(InspectionLocations.map((l) => Type.Literal(l))),
  inspectionAuthority: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }),
    Type.Null(),
  ]),

  // Free-form
  remarks: Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()]),

  // Workflow state (stored)
  partsWorkflowState: Type.Union(PartsWorkflowStates.map((s) => Type.Literal(s))),
  partsFinalisedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  partsFinalisedByUserId: Type.Union([UuidSchema, Type.Null()]),

  // Closure
  ndcRequired: Type.Boolean(),
  ndcIssuedDate: Type.Union([IsoDateSchema, Type.Null()]),
  ndcReferenceNumber: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.REFERENCE_NUMBER_MAX }),
    Type.Null(),
  ]),
  ndcRemarks: Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()]),
  ndcIssuedByUserId: Type.Union([UuidSchema, Type.Null()]),
  projectClosedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  projectClosedByUserId: Type.Union([UuidSchema, Type.Null()]),

  // Provenance
  createdByUserId: UuidSchema,

  ...TimestampFields,
  ...SoftDeleteFields,
  ...RowVersionField,
});
export type Project = Static<typeof ProjectSchema>;

// ---------------------------------------------------------------------------
// Project with derived fields — API surface for project detail / list
// ---------------------------------------------------------------------------

export const ProjectWithStatusSchema = Type.Composite([
  ProjectSchema,
  Type.Object({
    /** Derived lifecycle state (see §5.7 / §15 of the spec). */
    lifecycleState: Type.Union(ProjectLifecycleStates.map((s) => Type.Literal(s))),
    /** Days the project has been in its current lifecycle state. */
    daysInLifecycleState: Type.Integer({ minimum: 0 }),
    /** Whether the project is overdue (Effective Delivery Target + Grace past, with parts incomplete). */
    isOverdue: Type.Boolean(),
    /** Computed Warranty End — null until every part has a Delivered date. */
    warrantyEndDate: Type.Union([IsoDateSchema, Type.Null()]),
    /** Project-level GST amount derived from totalStoreValuePaisa × gstRateBps. */
    gstAmountPaisa: Type.Union([PaisaSchema, Type.Null()]),
    /** Project-level declared total contract value: total store value + GST amount. */
    declaredTotalContractValuePaisa: Type.Union([PaisaSchema, Type.Null()]),
    /** Computed contract value (sum across parts of qty × client_unit_price), paisa. */
    contractValuePaisa: PaisaSchema,
    /** Computed cost (suppliers side), paisa. */
    costPaisa: PaisaSchema,
    /**
     * Computed margin (spec §6.1 — contract value − cost), paisa.
     *
     * Signed: NOT PaisaSchema. If cost exceeds contract value (a loss-making
     * project, or a project priced before supplier costs were finalised),
     * margin is negative. The Financials tab surfaces this with a coloured
     * tile so a loss-making project is obvious at a glance.
     */
    marginPaisa: Type.Integer(),
  }),
]);
export type ProjectWithStatus = Static<typeof ProjectWithStatusSchema>;

// ---------------------------------------------------------------------------
// Create / update inputs
// ---------------------------------------------------------------------------

/**
 * Create input. DFA Received is mandatory; everything else is optional and may be
 * back-filled or set later.
 */
export const ProjectCreateSchema = Type.Object({
  projectTitle: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.PROJECT_TITLE_MAX }),
  projectNumber: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.PROJECT_NUMBER_MAX }),
  clientId: UuidSchema,

  // Currency / conditional foreign principal details
  projectCurrency: Type.Optional(Type.Union(ProjectCurrencies.map((c) => Type.Literal(c)))),
  otherCurrencyCode: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.CURRENCY_CODE_MAX })),
  principalName: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NAME_MAX })),
  principalAddress: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX })),
  principalContactPerson: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NAME_MAX })),
  principalEmail: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX })),
  principalPhone: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX })),
  principalFax: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX })),
  portOfShipment: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NAME_MAX })),

  // Conditional foreign principal bank details
  principalBankName: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NAME_MAX })),
  principalBankAccountNumber: Type.Optional(
    Type.String({ maxLength: FIELD_LIMITS.BANK_ACCOUNT_MAX }),
  ),
  principalBankIban: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.BANK_ACCOUNT_MAX })),
  principalBankAccountTitle: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NAME_MAX })),
  principalBankSwiftCode: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.SWIFT_CODE_MAX })),
  principalBankAddress: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX })),
  principalBankEmail: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX })),
  principalBankPhone: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX })),
  principalBankFax: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX })),

  dfaReceivedDate: IsoDateSchema,
  dfaSubmittedDate: Type.Optional(IsoDateSchema),
  contractSignDate: Type.Optional(IsoDateSchema),
  dateOfIssuance: Type.Optional(IsoDateSchema),
  technicalBidOpeningDate: Type.Optional(IsoDateSchema),
  financialBidOpeningDate: Type.Optional(IsoDateSchema),
  deliveryDurationMonths: Type.Optional(
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.DELIVERY_DURATION_MONTHS_MAX }),
  ),
  origin: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.ORIGIN_MAX })),
  indentNumber: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.INDENT_NUMBER_MAX })),
  indentDate: Type.Optional(IsoDateSchema),
  makerNameBrand: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.MAKER_NAME_BRAND_MAX })),
  totalStoreValuePaisa: Type.Optional(PaisaSchema),
  gstRateBps: Type.Optional(
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
  ),
  requiredBgAmountPaisa: Type.Optional(PaisaSchema),
  bgSubmissionDurationDays: Type.Optional(
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.BG_SUBMISSION_DAYS_MAX }),
  ),
  bgValidityDurationMonths: Type.Optional(
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.BG_VALIDITY_MONTHS_MAX }),
  ),
  termsOfDelivery: Type.Optional(
    Type.String({ maxLength: FIELD_LIMITS.INCOTERM_CODE_MAX }),
  ),
  paymentOnINoteBps: Type.Optional(
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
  ),
  paymentOnCrvBps: Type.Optional(
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
  ),
  partialDeliveryAllowed: Type.Optional(Type.Boolean()),
  partialPaymentAllowed: Type.Optional(Type.Boolean()),
  partialDeliveryPaymentRemarks: Type.Optional(
    Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }),
  ),
  gracePeriodDays: Type.Optional(
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.GRACE_DAYS_MAX }),
  ),
  earnestMoneyAmountPaisa: Type.Optional(PaisaSchema),
  warrantyPeriodMonths: Type.Optional(
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.WARRANTY_MONTHS_MAX }),
  ),
  consigneeLocation: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX })),
  defaultInspectionLocation: Type.Optional(
    Type.Union(InspectionLocations.map((l) => Type.Literal(l))),
  ),
  inspectionAuthority: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NAME_MAX })),
  remarks: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX })),

  ndcRequired: Type.Optional(Type.Boolean()),
  ndcReferenceNumber: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.REFERENCE_NUMBER_MAX })),
  ndcRemarks: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX })),
});
export type ProjectCreate = Static<typeof ProjectCreateSchema>;

/**
 * General-purpose update. Date progression validation (DFA sequence,
 * past-tense ≤ today) is enforced server-side.
 */
export const ProjectUpdateSchema = Type.Object({
  projectTitle: Type.Optional(
    Type.String({ minLength: 1, maxLength: FIELD_LIMITS.PROJECT_TITLE_MAX }),
  ),
  projectNumber: Type.Optional(
    Type.String({ minLength: 1, maxLength: FIELD_LIMITS.PROJECT_NUMBER_MAX }),
  ),
  clientId: Type.Optional(UuidSchema),

  // Currency / conditional foreign principal details
  projectCurrency: Type.Optional(Type.Union(ProjectCurrencies.map((c) => Type.Literal(c)))),
  otherCurrencyCode: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.CURRENCY_CODE_MAX }), Type.Null()]),
  ),
  principalName: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  ),
  principalAddress: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX }), Type.Null()]),
  ),
  principalContactPerson: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  ),
  principalEmail: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX }), Type.Null()]),
  ),
  principalPhone: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  ),
  principalFax: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  ),
  portOfShipment: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  ),

  // Conditional foreign principal bank details
  principalBankName: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  ),
  principalBankAccountNumber: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.BANK_ACCOUNT_MAX }), Type.Null()]),
  ),
  principalBankIban: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.BANK_ACCOUNT_MAX }), Type.Null()]),
  ),
  principalBankAccountTitle: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  ),
  principalBankSwiftCode: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.SWIFT_CODE_MAX }), Type.Null()]),
  ),
  principalBankAddress: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX }), Type.Null()]),
  ),
  principalBankEmail: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.EMAIL_MAX }), Type.Null()]),
  ),
  principalBankPhone: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  ),
  principalBankFax: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.PHONE_MAX }), Type.Null()]),
  ),

  dfaReceivedDate: Type.Optional(IsoDateSchema),
  dfaSubmittedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  contractSignDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  dateOfIssuance: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  technicalBidOpeningDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  financialBidOpeningDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),

  deliveryDurationMonths: Type.Optional(
    Type.Union([
      Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.DELIVERY_DURATION_MONTHS_MAX }),
      Type.Null(),
    ]),
  ),
  origin: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.ORIGIN_MAX }), Type.Null()]),
  ),
  indentNumber: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.INDENT_NUMBER_MAX }), Type.Null()]),
  ),
  indentDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  makerNameBrand: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.MAKER_NAME_BRAND_MAX }), Type.Null()]),
  ),
  totalStoreValuePaisa: Type.Optional(Type.Union([PaisaSchema, Type.Null()])),
  gstRateBps: Type.Optional(
    Type.Union([
      Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
      Type.Null(),
    ]),
  ),
  requiredBgAmountPaisa: Type.Optional(Type.Union([PaisaSchema, Type.Null()])),
  bgSubmissionDurationDays: Type.Optional(
    Type.Union([
      Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.BG_SUBMISSION_DAYS_MAX }),
      Type.Null(),
    ]),
  ),
  bgValidityDurationMonths: Type.Optional(
    Type.Union([
      Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.BG_VALIDITY_MONTHS_MAX }),
      Type.Null(),
    ]),
  ),
  termsOfDelivery: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.INCOTERM_CODE_MAX }), Type.Null()]),
  ),
  paymentOnINoteBps: Type.Optional(
    Type.Union([
      Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
      Type.Null(),
    ]),
  ),
  paymentOnCrvBps: Type.Optional(
    Type.Union([
      Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
      Type.Null(),
    ]),
  ),
  partialDeliveryAllowed: Type.Optional(Type.Boolean()),
  partialPaymentAllowed: Type.Optional(Type.Boolean()),
  partialDeliveryPaymentRemarks: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()]),
  ),
  gracePeriodDays: Type.Optional(
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.GRACE_DAYS_MAX }),
  ),

  earnestMoneyAmountPaisa: Type.Optional(Type.Union([PaisaSchema, Type.Null()])),
  earnestSubmittedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  earnestReleasedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),

  warrantyPeriodMonths: Type.Optional(
    Type.Union([
      Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.WARRANTY_MONTHS_MAX }),
      Type.Null(),
    ]),
  ),

  consigneeLocation: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.ADDRESS_MAX }), Type.Null()]),
  ),
  defaultInspectionLocation: Type.Optional(
    Type.Union(InspectionLocations.map((l) => Type.Literal(l))),
  ),
  inspectionAuthority: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NAME_MAX }), Type.Null()]),
  ),
  remarks: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()]),
  ),

  // Closure-related fields (allowed for users with permission)
  ndcRequired: Type.Optional(Type.Boolean()),
  ndcIssuedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  ndcReferenceNumber: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.REFERENCE_NUMBER_MAX }), Type.Null()]),
  ),
  ndcRemarks: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()]),
  ),

  rowVersion: Type.Integer({ minimum: 0 }),
});
export type ProjectUpdate = Static<typeof ProjectUpdateSchema>;

/**
 * Setting Project Closed is its own action (separate audit event).
 *
 * Optional closure notes are captured as audit metadata for the
 * CLOSE_PROJECT action.
 */
export const ProjectCloseSchema = Type.Object({
  rowVersion: Type.Integer({ minimum: 0 }),
  closureNotes: Type.Optional(Type.String({ maxLength: 2000 })),
  /**
   * Pass 26 — when closure preconditions aren't met (some part hasn't
   * reached stage 18, active batch in transit, or BG still active), the
   * first close attempt fails with WARNING_PROJECT_INCOMPLETE describing
   * the open items. The UI surfaces a confirmation dialog and retries
   * with this flag set to true. Reflects spec §19(g): NDC / BG Released /
   * Project Closed are independent events that may be recorded out-of-
   * order with a warning, not a hard block.
   */
  acknowledgeIncompleteClosure: Type.Optional(Type.Boolean()),
});
export type ProjectClose = Static<typeof ProjectCloseSchema>;
